import { GameEvents } from '../GameEvents';
import { getLevelManifest } from '../levelManifest';
import type EventBus from '../EventBus';
import type { UiState } from '../GameManager';
import type { LevelDataService } from '../services/LevelDataService';
import getLogger from '../logger';
import LevelManager from '../LevelManager';

const logger = getLogger('LevelHandler');

export default class LevelHandler {
  private state: Readonly<UiState>;
  private eventBus: EventBus;
  private dataService: LevelDataService;
  private levelConfigs: Record<string, any>;
  currentLevelManager: any = null;  // Public for testing

  constructor(state: Readonly<UiState>, eventBus: EventBus, dataService: LevelDataService) {
    this.state = state;
    this.eventBus = eventBus;
    this.dataService = dataService;

    // Build level configs from manifest
    this.levelConfigs = {};
    const allLevelIds = getLevelManifest();
    for (const levelId of allLevelIds) {
      this.levelConfigs[levelId] = {
        key: levelId,
        data: () => this.dataService.getLevelData(levelId),
        theme: 'default',
      };
    }

    this.eventBus.on(GameEvents.PLAY_LEVEL, (payload: any) => this._onPlayLevel(payload));
    this.eventBus.on(GameEvents.PLAY_LEVEL_FROM_DATA, (payload: any) => this._onPlayLevelFromData(payload));

    // Subscribe to LevelManager events
    this.eventBus.on(GameEvents.PLAYER_ACTION_TAKEN, () => this._onPlayerActionTaken());
    this.eventBus.on(GameEvents.ACTION_MODE_CHANGED, () => this._onActionModeChanged());
    this.eventBus.on(GameEvents.ACTION_MODES_UPDATED, () => this._onActionModesUpdated());
    this.eventBus.on(GameEvents.UNDO_LAST_MOVE, () => this._onUndoLastMove());

    // Subscribe to InputSystem events
    this.eventBus.on(GameEvents.SET_ACTION_MODE, (mode: any) => this._onSetActionMode(mode));
    this.eventBus.on(GameEvents.PLAYER_ACTION_REQUESTED, (action: any) => this._onPlayerActionRequested(action))
    this.eventBus.on(GameEvents.RECORD_PLAYER_ACTION, (action: any) => this._onRecordPlayerAction(action))
    this.eventBus.on(GameEvents.REPLAY_SOLUTION_ACTION, (relativeAction: any) => this._onReplaySolutionAction(relativeAction))

    // Subscribe to Scene input events
    this.eventBus.on(GameEvents.PLAYER_TAPPED_HEX, (payload: any) => this._onPlayerTappedHex(payload));
  }

  _onStartLevel({ key, data }: { key: string; data: any }) {
    logger.info('Starting level', key);

    // Clean up previous level manager
    if (this.currentLevelManager) {
      this.currentLevelManager.destroy();
    }

    // Create LevelManager - constructs all systems, loads entities, emits LEVEL_STARTED
    this.currentLevelManager = new LevelManager(this.eventBus.getEmitter(), data, key);

    // Start the game loop - runs until player's turn
    // SpatialHandler (if present) will have created scene reactively from PREPARE_SCENE
    this.currentLevelManager.start();
  }

  async _onPlayLevel({ levelKey }: { levelKey: string }) {
    logger.info('playLevel', levelKey);
    const levelConfigTemplate = this.levelConfigs[levelKey];
    if (!levelConfigTemplate) {
      logger.error(`Level ${levelKey} not found.`);
      return;
    }

    const levelModule = typeof levelConfigTemplate.data === 'function'
      ? levelConfigTemplate.data()
      : { default: levelConfigTemplate.data };
    const levelData = levelModule.default || levelModule;

    await this._prepareAndStart(levelKey, levelData, levelConfigTemplate.theme || 'default');
  }

  async _onPlayLevelFromData({ levelKey, levelData }: { levelKey: string; levelData: any }) {
    logger.info('playLevelFromData', levelKey);

    // If levelData is just tutorial metadata (not a full level), emit events but skip level creation
    if (levelData && !levelData.collections && levelData.tutorial) {
      const tutorial = levelData.tutorial;
      this.eventBus.emit(GameEvents.LEVEL_PREPARED, { levelKey, levelData: null, tutorial });
      this.eventBus.emit(GameEvents.SET_SCREEN, 'playing');
      return;
    }

    await this._prepareAndStart(levelKey, levelData, 'default');
  }

  async _prepareAndStart(levelKey: string, levelData: any, themeName: string) {
    // Extract tutorial from level data (with safety checks for test data)
    const collections = levelData?.collections;
    const entities = (collections && collections[0]?.data) || [];
    const metaEntity = Array.isArray(entities) ? entities.find((e: any) => e?.tutorial) : null;
    const tutorial = metaEntity ? metaEntity.tutorial : null;

    // Emit LEVEL_PREPARED synchronously – TutorialHandler reacts before the setTimeout
    this.eventBus.emit(GameEvents.LEVEL_PREPARED, { levelKey, levelData, tutorial });
    this.eventBus.emit(GameEvents.SET_SCREEN, 'playing');

    // Defer heavy game init to next tick so tutorial modal can render first
    setTimeout(() => {
      // Signal SpatialHandler to prepare the scene (if rendering)
      this.eventBus.emit(GameEvents.PREPARE_SCENE, {
        key: levelKey,
        data: levelData,
        themeName,
      });

      // Create LevelManager (game logic)
      this._onStartLevel({ key: levelKey, data: levelData });
    }, 0);
  }

  // Delegate to LevelManager
  async _onPlayerActionTaken() {
    if (this.currentLevelManager) {
      await this.currentLevelManager.advanceAfterPlayerAction();
    } else {
      logger.warn('PLAYER_ACTION_TAKEN event received but no level is loaded');
    }
  }

  _onActionModeChanged() {
    if (this.currentLevelManager) {
      this.currentLevelManager.updateActionHighlights();
    } else {
      logger.warn('ACTION_MODE_CHANGED event received but no level is loaded');
    }
  }

  _onActionModesUpdated() {
    if (this.currentLevelManager) {
      this.currentLevelManager.updateActionHighlights();
    } else {
      logger.warn('ACTION_MODES_UPDATED event received but no level is loaded');
    }
  }

  async _onUndoLastMove() {
    if (this.currentLevelManager) {
      await this.currentLevelManager.undoLastMove();
    } else {
      logger.warn('UNDO_LAST_MOVE event received but no level is loaded');
    }
  }

  // Delegate to InputSystem via LevelManager
  _onSetActionMode(mode: string) {
    if (this.currentLevelManager) {
      this.currentLevelManager.setActionMode(mode);
    } else {
      logger.warn('SET_ACTION_MODE event received but no level is loaded');
    }
  }

  _onPlayerActionRequested(action: any) {
    if (this.currentLevelManager) {
      this.currentLevelManager.submitPlayerAction(action);
    } else {
      logger.warn('PLAYER_ACTION_REQUESTED event received but no level is loaded');
    }
  }

  _onRecordPlayerAction(action: any) {
    if (this.currentLevelManager) {
      this.currentLevelManager.recordPlayerAction(action);
    } else {
      logger.warn('RECORD_PLAYER_ACTION event received but no level is loaded');
    }
  }

  _onReplaySolutionAction(relativeAction: any) {
    if (this.currentLevelManager) {
      this.currentLevelManager.submitRelativeAction(relativeAction);
    } else {
      logger.warn('REPLAY_SOLUTION_ACTION event received but no level is loaded');
    }
  }

  // Scene input handlers
  _onPlayerTappedHex({ hexCoords }: { hexCoords: { q: number; r: number } }) {
    // TODO: law of demeter. this should call currentLevelManager.handleTap which should forward
    if (this.currentLevelManager?.inputSystem) {
      this.currentLevelManager.inputSystem.handleTap(hexCoords);
    }
  }

}
